import { Package, FileText, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const paths = [
  {
    icon: Package,
    label: "Start Here: Complete DIY Kit",
    badge: "Best Value",
    price: "$99",
    description: "Everything you need in one package",
    cta: "Get the Kit",
    anchor: "#kit-section",
    primary: true,
  },
  {
    icon: FileText,
    label: "I need one specific letter",
    price: "$29.99",
    description: "Pick the template that matches your situation",
    cta: "Browse Templates",
    anchor: "#templates-section",
    primary: false,
  },
];

const ChooseYourPath = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  const handleClick = (anchor?: string) => {
    if (anchor) {
      const element = document.querySelector(anchor);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="py-12 lg:py-16 bg-background">
      <div className="container-custom">
        <div
          ref={ref}
          className={cn(
            "text-center mb-10 transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Choose Your <span className="text-gradient">Path</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Not sure where to start? Pick the option that fits your situation.
          </p>
        </div>

        <div className={cn(
          "grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto stagger-children",
          isVisible && "visible"
        )}>
          {paths.map((path, index) => {
            const Icon = path.icon;
            return (
              <div
                key={index}
                className={cn(
                  "relative bg-card rounded-2xl p-6 border transition-all duration-300 hover:shadow-custom-md hover:-translate-y-2 flex flex-col group",
                  path.primary
                    ? "border-primary/50 shadow-custom-sm ring-2 ring-primary/20 border-glow-animated"
                    : "border-border/50 shadow-custom-sm hover:border-primary/30"
                )}
              >
                {path.badge && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground animate-pulse-soft">
                    {path.badge}
                  </Badge>
                )}

                <div
                  className={cn(
                    "w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110",
                    path.primary ? "bg-primary/10 group-hover:bg-primary/20" : "bg-muted group-hover:bg-primary/10"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-7 h-7 transition-colors duration-300",
                      path.primary ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                    )}
                  />
                </div>

                <h3 className="font-bold text-lg text-foreground mb-1">
                  {path.label}
                </h3>
                <p className="text-2xl font-bold text-primary mb-2 number-highlight">{path.price}</p>
                <p className="text-sm text-muted-foreground mb-6 flex-grow">
                  {path.description}
                </p>

                <Button
                  variant={path.primary ? "gold" : "outline"}
                  className="w-full group/btn"
                  onClick={() => handleClick(path.anchor)}
                >
                  {path.cta}
                  <ArrowRight className="w-4 h-4 ml-2 opacity-0 -translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ChooseYourPath;
